import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subscriptionAPI, statusAPI } from '../utils/api';
import { SUBSCRIPTION_PLANS, PlanType, isPlanType } from '../constants/subscriptionPlans';
import './SubscriptionPage.scss';

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string; sessionId?: number } | null)?.returnTo;
  const sessionId = (location.state as { returnTo?: string; sessionId?: number } | null)?.sessionId;
  const params = new URLSearchParams(location.search);
  const planFromQuery = params.get('plan');
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(
    isPlanType(planFromQuery) ? planFromQuery : null
  );
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [stripeTestMode, setStripeTestMode] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const confirmTried = useRef(false);

  // Require auth before checkout
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      const redirect = encodeURIComponent(`/subscription${location.search || ''}`);
      navigate(`/login?redirect=${redirect}`, { replace: true });
    }
  }, [navigate, location.search]);

  useEffect(() => {
    statusAPI.get()
      .then(({ data }) => setStripeTestMode(!!data?.stripeTestMode))
      .catch(() => setStripeTestMode(false));
  }, []);

  useEffect(() => {
    if (isPlanType(planFromQuery)) setSelectedPlan(planFromQuery);
  }, [planFromQuery]);

  // After Stripe redirect: confirm session (covers webhook delay), then go to dashboard/chat
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    if (search.get('canceled') === '1') {
      setCanceled(true);
      return;
    }
    if (search.get('success') !== '1' || confirmTried.current) return;
    confirmTried.current = true;

    const stripeSessionId = search.get('session_id');
    const finish = () => {
      if (returnTo === 'chat' && sessionId != null) {
        navigate('/chat', { state: { sessionId }, replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    if (!stripeSessionId) {
      finish();
      return;
    }

    setConfirming(true);
    subscriptionAPI
      .confirmSession(stripeSessionId)
      .catch((err) => {
        console.warn('Confirm session failed (webhook may still activate):', err?.response?.data || err);
      })
      .finally(() => {
        setConfirming(false);
        finish();
      });
  }, [location.search, returnTo, sessionId, navigate]);

  const plans = SUBSCRIPTION_PLANS.map((p) => ({
    type: p.type,
    name: t(`subscription.plans.${p.type}`),
    price: p.priceDisplay,
    pricePeriod:
      p.interval === 'once'
        ? t('subscription.oneTime')
        : t(p.interval === 'year' ? 'subscription.perYear' : 'subscription.perMonth'),
    features: t(`subscription.features.${p.type}`),
    featured: p.featured,
  }));

  const handlePurchase = async () => {
    if (!selectedPlan) return;
    if (!localStorage.getItem('token')) {
      const redirect = encodeURIComponent(`/subscription?plan=${selectedPlan}`);
      navigate(`/login?redirect=${redirect}`);
      return;
    }

    setLoading(true);
    try {
      const { data } = await subscriptionAPI.createCheckoutSession(selectedPlan);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('No checkout URL');
    } catch (error: any) {
      if (error?.response?.status === 503) {
        alert(t('subscription.errors.stripeNotConfigured'));
      } else {
        const msg = error?.response?.data?.error || t('subscription.errors.purchaseFailed');
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (confirming) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__container">
          <p>{t('subscription.confirming')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-page__container">
        <button
          type="button"
          className="page-back"
          onClick={() => navigate(returnTo === 'chat' ? '/chat' : '/dashboard', returnTo === 'chat' && sessionId != null ? { state: { sessionId } } : undefined)}
          aria-label={t('nav.back')}
        >
          ← {t('nav.back')}
        </button>
        <h1 className="subscription-page__title">{t('subscription.title')}</h1>
        <p className="subscription-page__subtitle">{t('subscription.subtitle')}</p>

        {stripeTestMode && (
          <div className="subscription-page__virtual-banner" role="status">
            {t('subscription.virtualPaymentBanner')}
          </div>
        )}

        {canceled && (
          <div className="subscription-page__virtual-banner" role="status">
            {t('subscription.canceledNotice')}
          </div>
        )}

        <div className="subscription-plans">
          {plans.map((plan) => (
            <div
              key={plan.type}
              className={`subscription-plan ${selectedPlan === plan.type ? 'subscription-plan--selected' : ''} ${plan.featured ? 'subscription-plan--featured' : ''}`}
              onClick={() => setSelectedPlan(plan.type)}
            >
              <h3>{plan.name}</h3>
              <div className="subscription-plan__price">
                <span className="price-amount">{plan.price} €</span>
                <span className="price-period">{plan.pricePeriod}</span>
              </div>
              <p className="subscription-plan__features-text">{plan.features}</p>
            </div>
          ))}
        </div>

        <div className="subscription-page__actions">
          <button
            className="btn-primary btn-primary--large"
            onClick={handlePurchase}
            disabled={!selectedPlan || loading}
          >
            {loading ? t('subscription.purchasing') : t('subscription.purchase')}
          </button>
          <button
            className="btn-secondary"
            onClick={() => navigate(returnTo === 'chat' ? '/chat' : '/dashboard', returnTo === 'chat' && sessionId != null ? { state: { sessionId } } : undefined)}
          >
            {t('subscription.cancel')}
          </button>
        </div>

        <p className="subscription-page__note">
          {stripeTestMode ? t('subscription.note') : t('subscription.noteLive')}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPage;
